import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Row,
  Col,
  Checkbox,
  Space,
  message,
  Image,
  Select,
  Table,
  InputNumber
} from "antd";
import {
  PlusOutlined,
  MinusCircleOutlined,
  ShoppingCartOutlined
} from "@ant-design/icons";
import { useParams, useHistory } from "react-router-dom";
import request from "helper/request";
import "./PublicPurchaseOrderPage.scss";

const { Title, Text, Link } = Typography;
const { TextArea } = Input;

const PublicPurchaseOrderPage = () => {
  const { clientId } = useParams();
  const history = useHistory();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [clientLoading, setClientLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [resolvedClientId, setResolvedClientId] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(false);
  const [logoReady, setLogoReady] = useState(false);
  const [displayInventoryOnPOs, setDisplayInventoryOnPOs] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState({});

  useEffect(() => {
    resolveClient();
  }, [clientId]);

  const resolveClient = async () => {
    setClientLoading(true);
    setLogoReady(false);
    try {
      // Try to resolve the client by UUID or custom_id
      const response = await request.get(`/public/resolve-client/${clientId}`);

      // Note: request interceptor returns response.data directly
      if (response.success && response.client) {
        setClient(response.client);
        setResolvedClientId(response.client.id);

        // Check for whitelabel logo
        if (response.client.logoUrl && response.client.whitelabelEnabled) {
          setLogoUrl(response.client.logoUrl);
          setWhitelabelEnabled(true);
        }

        // Check for inventory display setting (now returned from resolve-client)
        const displayInventory = response.client.displayInventoryOnPOs === 1;
        setDisplayInventoryOnPOs(displayInventory);

        // If enabled, fetch inventory items
        if (displayInventory) {
          await fetchInventoryItems(response.client.id);
        }

        // Mark logo as ready to display
        setLogoReady(true);
        setClientLoading(false);
      } else {
        // Client not found, redirect (keep loading state to prevent flash)
        window.location.href = "https://www.goflexio.com";
      }
    } catch (error) {
      console.error("Error resolving client:", error);
      // Redirect to goflexio.com on error (keep loading state to prevent flash)
      window.location.href = "https://www.goflexio.com";
    }
  };

  const fetchInventoryItems = async (resolvedId) => {
    setInventoryLoading(true);
    try {
      // Fetch all inventory items from all warehouses using public endpoint
      const response = await request.get(`/public/inventory/items/${resolvedId}`);

      if (response.success && response.items) {
        setInventoryItems(response.items);
      }
    } catch (error) {
      console.error("Error fetching inventory items:", error);
      message.error("Failed to load inventory items");
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleQuantityChange = (itemId, quantity) => {
    if (quantity > 0) {
      setSelectedProducts((prev) => ({
        ...prev,
        [itemId]: quantity
      }));
    } else {
      const newSelected = { ...selectedProducts };
      delete newSelected[itemId];
      setSelectedProducts(newSelected);
    }
  };

  const onFinish = async (values) => {
    if (!resolvedClientId) {
      message.error("Client not found");
      return;
    }

    // For inventory mode, convert selectedProducts to products array
    let productsToSubmit;
    if (displayInventoryOnPOs) {
      productsToSubmit = Object.entries(selectedProducts).map(([itemId, qty]) => {
        const item = inventoryItems.find((i) => i.id === itemId);
        return {
          qty,
          description: item ? `${item.name} (${item.itemId})` : itemId,
          inventoryItemId: itemId, // Save the UUID
          inventoryItemName: item?.name,
          inventoryItemCode: item?.itemId
        };
      });

      console.log("📦 Products to submit (inventory mode):", productsToSubmit);

      if (productsToSubmit.length === 0) {
        message.error("Please select at least one product");
        return;
      }
    } else {
      productsToSubmit = values.products;
      console.log("📦 Products to submit (generic mode):", productsToSubmit);
    }

    setLoading(true);
    try {
      const payload = {
        clientId: resolvedClientId,
        contactName: values.contactName,
        contactEmail: values.contactEmail,
        vendorCompanyName: values.vendorCompanyName,
        vendorId: values.vendorId,
        products: productsToSubmit,
        specialNotes: values.specialNotes,
        acceptedTerms: values.acceptedTerms
      };

      console.log("📤 Sending purchase order payload:", payload);

      const response = await request.post("/purchase-order", payload);

      // Note: request interceptor returns response.data directly
      if (response.success) {
        message.success("Purchase order submitted successfully!");
        form.resetFields();
        setSelectedProducts({});
        // Optionally redirect or show success message
      }
    } catch (error) {
      console.error("Error submitting purchase order:", error);
      message.error("Failed to submit purchase order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {clientLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#ffffff",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Card loading bordered={false} style={{ background: "transparent" }} />
        </div>
      )}
      <div
        className="public-purchase-order-page"
        style={{ opacity: clientLoading ? 0 : 1 }}
      >
        <Row
          justify="center"
          style={{ minHeight: "100vh", background: "#f0f2f5", padding: "40px 20px" }}
        >
          <Col xs={24} sm={20} md={16} lg={12} xl={10}>
            <Card>
              <div style={{ textAlign: "center", marginBottom: "32px" }}>
                {logoReady ? (
                  <Image
                    src={
                      whitelabelEnabled && logoUrl
                        ? logoUrl
                        : `/logos/${window.REACT_APP_LOGO_FILE_NAME || "flexio-logo.png"}`
                    }
                    alt="Logo"
                    preview={false}
                    style={{
                      maxWidth: "200px",
                      maxHeight: "80px",
                      objectFit: "contain"
                    }}
                  />
                ) : (
                  <div style={{ height: "80px" }} />
                )}
                <Title level={2} style={{ marginTop: "24px" }}>
                  Submit Purchase Order
                </Title>
                {client && <Text type="secondary">For: {client.clientName}</Text>}
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{
                  products: [{ qty: 1, description: "" }]
                }}
              >
                <Form.Item
                  label="Contact Name"
                  name="contactName"
                  rules={[{ required: true, message: "Please enter your name" }]}
                >
                  <Input placeholder="John Doe" />
                </Form.Item>

                <Form.Item
                  label="Email Address"
                  name="contactEmail"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" }
                  ]}
                >
                  <Input placeholder="john.doe@example.com" />
                </Form.Item>

                <Form.Item
                  label="Vendor / Company Name"
                  name="vendorCompanyName"
                  rules={[
                    { required: true, message: "Please enter vendor/company name" }
                  ]}
                >
                  <Input placeholder="ACME Corp" />
                </Form.Item>

                <Form.Item label="Vendor ID (Optional)" name="vendorId">
                  <Input placeholder="V12345" />
                </Form.Item>

                <div style={{ marginBottom: "24px" }}>
                  <Text strong>Products Requested</Text>
                  {displayInventoryOnPOs ? (
                    <div style={{ marginTop: "12px" }}>
                      <Table
                        dataSource={inventoryItems}
                        loading={inventoryLoading}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        columns={[
                          {
                            title: "Image",
                            dataIndex: "image",
                            key: "image",
                            width: 80,
                            render: (image, record) =>
                              image ? (
                                <Image
                                  src={image}
                                  alt={record.name}
                                  width={60}
                                  height={60}
                                  style={{ objectFit: "cover", borderRadius: "4px" }}
                                  preview={true}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    backgroundColor: "#f0f0f0",
                                    borderRadius: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                  }}
                                >
                                  <ShoppingCartOutlined
                                    style={{ fontSize: "24px", color: "#bbb" }}
                                  />
                                </div>
                              )
                          },
                          {
                            title: "Item ID",
                            dataIndex: "itemId",
                            key: "itemId",
                            width: 120
                          },
                          {
                            title: "Product Name",
                            dataIndex: "name",
                            key: "name",
                            ellipsis: true
                          },
                          {
                            title: "Description",
                            dataIndex: "description",
                            key: "description",
                            ellipsis: true,
                            render: (desc) => desc || "-"
                          },
                          {
                            title: "Available",
                            dataIndex: "quantity",
                            key: "quantity",
                            width: 100,
                            align: "center"
                          },
                          {
                            title: "Unit Price",
                            dataIndex: "unitPrice",
                            key: "unitPrice",
                            width: 110,
                            align: "right",
                            render: (price) => `$${parseFloat(price).toFixed(2)}`
                          },
                          {
                            title: "Order Qty",
                            key: "orderQty",
                            width: 120,
                            align: "center",
                            render: (_, record) => (
                              <InputNumber
                                min={0}
                                max={record.quantity}
                                value={selectedProducts[record.id] || 0}
                                onChange={(value) =>
                                  handleQuantityChange(record.id, value)
                                }
                                placeholder="0"
                                style={{ width: "100%" }}
                              />
                            )
                          }
                        ]}
                      />
                      {Object.keys(selectedProducts).length > 0 && (
                        <div
                          style={{
                            marginTop: "12px",
                            padding: "12px",
                            backgroundColor: "#f6ffed",
                            border: "1px solid #b7eb8f",
                            borderRadius: "4px"
                          }}
                        >
                          <Text strong>
                            Selected: {Object.keys(selectedProducts).length} product(s)
                          </Text>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Form.List name="products">
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }) => (
                            <Space
                              key={key}
                              style={{ display: "flex", marginTop: "12px" }}
                              align="baseline"
                            >
                              <Form.Item
                                {...restField}
                                name={[name, "qty"]}
                                rules={[{ required: true, message: "Qty required" }]}
                                style={{ marginBottom: 0, width: "100px" }}
                              >
                                <Input type="number" placeholder="Qty" min={1} />
                              </Form.Item>
                              <Form.Item
                                {...restField}
                                name={[name, "description"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Product description required"
                                  }
                                ]}
                                style={{ marginBottom: 0, flex: 1 }}
                              >
                                <Input placeholder="Product description" />
                              </Form.Item>
                              {fields.length > 1 && (
                                <MinusCircleOutlined
                                  onClick={() => remove(name)}
                                  style={{ color: "#ff4d4f", fontSize: "18px" }}
                                />
                              )}
                            </Space>
                          ))}
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            style={{ marginTop: "12px" }}
                          >
                            Add Product
                          </Button>
                        </>
                      )}
                    </Form.List>
                  )}
                </div>

                <Form.Item label="Special Notes (Optional)" name="specialNotes">
                  <TextArea
                    rows={4}
                    placeholder="Any special delivery instructions or notes..."
                  />
                </Form.Item>

                <Form.Item
                  name="acceptedTerms"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(
                              new Error("You must accept the terms and conditions")
                            )
                    }
                  ]}
                >
                  <Checkbox>
                    I accept the{" "}
                    <a
                      href="https://www.goflexio.com/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms and conditions
                    </a>
                  </Checkbox>
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                  >
                    Submit Purchase Order
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default PublicPurchaseOrderPage;
